import React, { useMemo } from "react";
import Plot from "react-plotly.js";
import { Layout, PlotData } from "plotly.js";
import { Button, Skeleton } from "@mui/material";
import { UseQueryResult } from "react-query";
import styled from "styled-components";

import { usePoiTimeseries } from "src/hooks/results";
import { FilterItem, PoiTimeseries, PoiTimeseriesType } from "src/types";
import { Download as DownloadIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface Props {
  poiId: string | number;
  scenarios: FilterItem[];
  substanceId: string | number;
  type: PoiTimeseriesType;
}
export const TimeseriesGraph = React.memo((props: Props) => {
  const { scenarios } = props;
  const { t } = useTranslation();

  // fetch timeseries for every scenario
  const timeseriesResults = usePoiTimeseries(
    props.scenarios.map((s) => s.id),
    props.poiId,
    props.substanceId,
    props.type
  );

  /**
   * Memoisation
   */
  const traces = useMemo(() => {
    if (!timeseriesResults) return;
    if (timeseriesResults.some((t) => t.isLoading)) return [];

    // TODO: after the dummy scenario can be replaced, simplify this back to a map iteration (instead of reduce)
    const traces = timeseriesResults.reduce(
      (tracesAcc: Partial<PlotData>[], timeseriesResult: UseQueryResult<PoiTimeseries>) => {
        const { data: timeseries } = timeseriesResult;
        if (!timeseries) return tracesAcc;

        const plotData: Partial<PlotData> = {
          type: "scattergl",
          mode: "lines+markers",
          name: getScenarioName(scenarios, timeseries.scenarioId),
          x: timeseries.timeseriesList.map((serie) => {
            return new Date(serie.date);
          }),
          y: timeseries.timeseriesList.map((serie) => {
            return serie.value;
          }),
        };

        return [...tracesAcc, plotData];
      },
      []
    );

    return traces;
  }, [scenarios, timeseriesResults]);

  /**
   * Functions
   */
  function getScenarioName(scenarios: FilterItem[], id: number) {
    const result = scenarios.find((scenario) => scenario.id === id);
    if (result) return result.name;
    if (id === -99999) return "Dummy scenario to debug";
    else return "UKNOWN";
  }

  function handleDownload() {
    // naive implementation assuming the data-title attribute wont change...
    //
    // a better way could be this way:https://plotly.com/javascript/static-image-export/#saving-as-png
    // but at implementation time this needed the 'fs' module which is node.js specific
    const elem = document.querySelector('[data-title="Download plot as a png"]') as HTMLElement | null;
    if (elem) elem.click();
  }

  // render the component
  return render();

  function render() {
    if (!traces || !timeseriesResults) return null;

    // is data still loading?
    if (timeseriesResults.some((timeseriesResult) => timeseriesResult.isLoading)) {
      return <Skeleton variant="rectangular" width="100%" height="100%" animation="pulse" />;
    }

    const layout: Partial<Layout> = {
      autosize: true,
      legend: { orientation: "h" },
      xaxis: { automargin: true, zeroline: false },
      yaxis: {
        automargin: true,
        zeroline: false,
        tickformat: ".1f",
        ticksuffix: ` ${timeseriesResults[0].data?.unit}`,
      },
      showlegend: true,
      margin: { t: 20, b: 0, l: 0, r: 0 },
      updatemenus: [{}],
    };

    return (
      <Styles>
        <Plot style={{ width: "100%", height: "100%" }} data={traces} layout={layout} />

        <Button className="button-download" onClick={handleDownload} startIcon={<DownloadIcon />}>
          {t("label.downloadPlot")}
        </Button>
      </Styles>
    );
  }
});

const Styles = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  .button-download {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 999;
  }
`;
